import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import "../../style/customers.css";
import { useSelector } from "react-redux";
import { customersAPI } from "../../services";
import { toast } from "react-toastify";

function CustomerPrint() {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const hasPrintedRef = useRef(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all customers with all_data=True
  useEffect(() => {
    const fetchAllCustomers = async () => {
      setLoading(true);
      try {
        const result = await customersAPI.getCustomers(
          "", // searchTerm
          1, // page
          9999, // pageSize
          "", // filters
          "", // sortBy
          true // allData = True
        );

        if (result.error) {
          toast.error(result.error.message || "Failed to fetch customers");
          setCustomers([]);
        } else {
          const customersData = result.response?.data || [];
          setCustomers(customersData);
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Failed to fetch customers");
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllCustomers();
  }, []);

  // Helper function to format balance
  const formatBalance = (balance) => {
    const amount = parseFloat(balance || 0);
    return `€${amount.toFixed(2)}`;
  };

  // Helper function to check if balance is positive
  const isPositiveBalance = (balance) => {
    return parseFloat(balance || 0) >= 0;
  };

  // Auto-open print dialog when component loads and data is ready
  useEffect(() => {
    if (customers.length > 0 && !loading && !hasPrintedRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        window.print();
        hasPrintedRef.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [customers, loading]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate("/customers");
  };

  // Show loading state
  if (loading) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className={` ${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customers
          </button>
        </div>
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "1rem" }}>Loading customers data...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no customers
  if (!customers || customers.length === 0) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className={` ${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customers
          </button>
        </div>
        <div className="loading-container">
          <p>
            No customer data available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-invoice-container">
      {/* Header with Back and Print buttons */}
      <div className="print-invoice-header">
        <div>
          <button className={` ${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customers
          </button>
        </div>

        <div>
          <button className={` ${isPartsURL ? "print-print-btn-parts" : isAccessoriesURL ? "print-print-btn-acc" : "print-print-btn"}`} onClick={handlePrint}>
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Customer Table */}
      <div className={` ${isPartsURL ? "print-invoice-content-parts" : isAccessoriesURL ? "print-invoice-content-acc" : "print-invoice-content"}`}>
        {/* Invoice Header Info */}
        <div
          className="print-invoice-info"
          style={{ borderBottom: "1px solid #EAEEF2" }}
        >
          <div className="print-invoice-id" style={{ borderBottom: "none" }}>
            Customers List
          </div>
          <div className="print-invoice-date">
            Generated on: {new Date().toLocaleDateString()}
          </div>
        </div>

        <div className="customers-table-wrapper">
          <div className="customer-table-responsive">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>NAME</th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th>ADDRESS</th>
                  <th>BALANCE</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>{customer.id || "--"}</td>
                    <td>{customer.name || "--"}</td>
                    <td>{customer.user_email || customer.email || "--"}</td>
                    <td>{customer.mobile || customer.phone || "--"}</td>
                    <td>{customer.address || "--"}</td>
                    <td>
                      <span
                        className={`balance-badge ${
                          isPositiveBalance(customer.balance) ? "positive" : "negative"
                        }`}
                      >
                        {formatBalance(customer.balance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerPrint;
